import { useEffect, useRef, useState } from 'react';
import { toggleReportColumnSelection } from '@/api/reportTableColumns';

type ReportColumnOption = { id: string; label: string };

type ReportColumnPickerProps<T extends string> = {
  columns: readonly ReportColumnOption[];
  selected: T[];
  onChange: (next: T[]) => void;
  requiredColumnId?: T;
  requiredColumnIds?: readonly T[];
  panelTitle: string;
};

export function ReportColumnPicker<T extends string>({
  columns,
  selected,
  onChange,
  requiredColumnId,
  requiredColumnIds,
  panelTitle,
}: ReportColumnPickerProps<T>) {
  const requiredIds = [
    ...(requiredColumnId ? [requiredColumnId] : []),
    ...(requiredColumnIds ?? []),
  ];
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        className="rounded-pill border border-border bg-surface px-3 py-1.5 text-xs text-foreground-muted hover:bg-page"
      >
        Columns ({selected.length})
      </button>
      {open ? (
        <div className="absolute right-0 z-30 mt-2 w-44 rounded-xl border border-border bg-surface p-2 shadow-lg">
          <p className="mb-2 text-[11px] uppercase tracking-wide text-foreground-subtle">{panelTitle}</p>
          <div className="space-y-1.5">
            {columns.map((column) => {
              const columnId = column.id as T;
              const checked = selected.includes(columnId);
              const disableUncheck =
                checked && (requiredIds.includes(columnId) || selected.length === 1);
              return (
                <label key={column.id} className="flex items-center gap-2 text-xs text-foreground-muted">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disableUncheck}
                    onChange={(e) => {
                      onChange(
                        toggleReportColumnSelection(
                          selected,
                          columnId,
                          e.target.checked,
                          requiredIds
                        )
                      );
                    }}
                  />
                  {column.label}
                </label>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
